<!-- eslint-disable no-nested-ternary -->
<template>
  <b-modal
    id="tracker-info-modal"
    hide-header-close
    centered
  >
    <template #modal-title>
      <div
        class="d-flex align-items-center"
      >
        <span
          v-if="!isThirdPartyTags"
          class="icon pr-2"
        >
          <i
            v-if="isHealthy"
            :class="[statusType('success')]"
          />
          <i
            v-else
            v-for="(issueType, index) in issuesTypes.values()"
            :key="index"
            :class="[statusType(issueType.toLowerCase())]"
          />
        </span>
        {{ trackerType | title }}<span v-if="isGroup && !isThirdPartyTags">S</span>
      </div>
    </template>
    <!-- Body of the modal -->
    <div class="d-block text-left">
      <!-- Grouped Content-Level -->
      <template v-if="isContentLevel && isGroup">
        <template v-if="isHealthy">
          <p class="mb-0">
            All tags are healthy
          </p>
        </template>
        <div
          v-else
          v-for="(issue, index) in trackersIssues"
          :key="index"
        >
          <p
            class="font-weight-bold"
          >
            {{ issue.type }}
          </p>
          <ul class="list-unstyled">
            <li
              v-for="(title, titleIndex) in getIssueTitles(issue)"
              :key="titleIndex"
            >
              <p>
                {{ title }}
              </p>
            </li>
          </ul>
        </div>
      </template>
      <!-- Placement-Level Trackers (Grouped + Not Grouped) + Not Grouped Content-Level Trackers -->
      <template v-else-if="!isThirdPartyTags">
        <!-- Healthy Tracker/s (Tag/s) -->
        <template v-if="isHealthy">
          <p>
            <template
              v-if="isPlacementLevel"
            >
              {{ trackersInfo[0].value }}
            </template>
            <template v-else>
              Tag is Healthy
            </template>
          </p>
        </template>
        <!-- Warnings -->
        <template
          data-type="warning"
          v-else-if="isWarning"
        >
          <div>
            <template
              v-if="isNotMatchingURL"
              class="urlMatch"
            >
              <p><strong>{{ this.trackersIssues[0].type }}</strong></p>
              <b-row
                class="mb-4"
              >
                <b-col
                  cols="3"
                >
                  Destination:
                </b-col>
                <b-col
                  cols="9"
                  class="overflow-wrap"
                >
                  {{ this.trackersIssues[0].trackers[0].landingPage }}
                </b-col>
              </b-row>
              <b-row>
                <b-col
                  cols="3"
                >
                  Content:
                </b-col>
                <b-col
                  cols="9"
                  class="overflow-wrap"
                >
                  {{ trackersIssues[0].trackers[0].contentItem.url }}
                </b-col>
              </b-row>
            </template>
          </div>
        </template>
        <!-- Errors -->
        <template
          v-else
          data-type="error"
        >
          <p>
            <strong>{{ trackersIssues[0].type }}</strong>
          </p>
          <p>
            {{ trackersIssues[0].trackers[0].value }}
          </p>
        </template>
        <p v-if="isStatic">
          <strong>CLICK TAG IS STATIC</strong>
        </p>
      </template>
      <!-- 3rd-Party tags (Placement Level and Not Grouped Content Level) -->
      <template
        v-else
        class="thirdPartyTagsBody"
      >
        <p v-if="!trackersInfo.length">
          No Third Party Tags to Display
        </p>
        <template v-else>
          <div
            v-for="(tracker, index) in trackersInfo"
            :key="index"
            class="mb-5"
          >
            <p class="d-flex align-items-center mb-2">
              <span class="icon pr-2">
                <i
                  :class="[statusType(tracker.isValid ? 'success' : 'error')]"
                />
              </span>
              <strong>{{ tracker.type | title }}</strong>
            </p>
            <p class="mb-2">
              {{ tracker.value }}
            </p>
            <p
              v-if="!tracker.isValid"
              class="mb-0"
            >
              <strong>ERROR: {{ getErrorMsg(tracker) }}</strong>
            </p>
          </div>
        </template>
      </template>
    </div>
    <template #modal-footer="{ ok }">
      <b-button
        size="md"
        variant="primary"
        @click="ok()"
      >
        OK
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import _ from 'lodash';
import textHelper from '@/core/mixins/textHelper';

export default {
  name: 'TrackerInfoModal',
  mixins: [textHelper],
  props: {
    trackerType: {
      type: String,
      required: true,
      validator(value) {
        // The value must match one of these strings
        return ['impressionTracker', 'clickTracker', 'postClickTracker', 'thirdPartyTags'].includes(value);
      },
    },
    trackersInfo: {
      type: [Array, String, Object],
      required: true,
      default: () => [],
    },
    issuesTypes: {
      type: Set,
      required: true,
      default: () => new Set(),
    },
    trackersIssues: {
      type: Array,
      required: false,
      default: () => {},
    },
    scope: {
      type: String,
      required: true,
      default: 'CONTENT_ITEM',
    },
    isGroup: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  filters: {
    title: (text) => {
      const title = _.startCase(text).toUpperCase();
      if (title.includes('TRACKERS')) return title.replace('TRACKERS', 'TAG');
      if (title.includes('TRACKER')) return title.replace('TRACKER', 'TAG');
      return '3rd Party Tags';
    },
  },
  computed: {
    isContentLevel() {
      return this.scope === 'CONTENT_ITEM';
    },
    isPlacementLevel() {
      return this.scope === 'FEED';
    },
    isHealthy() {
      return this.issuesTypes.size === 0;
    },
    isWarning() {
      return this.issuesTypes.has('warning');
    },
    isStatic() {
      return this.trackersInfo.some((tracker) => tracker.isStatic);
    },
    isNotMatchingURL() {
      return this.trackersIssues[0].type.includes('URL Doesn\'t Match');
    },
    isThirdPartyTags() {
      return this.trackerType === 'thirdPartyTags';
    },
  },
  methods: {
    showModal() {
      this.$bvModal.show('tracker-info-modal');
    },
    isStaticClickTracker(issue) {
      return this.trackerType === 'clickTracker' && issue.type === 'WARNING' && issue.title === 'Static URL';
    },
    getErrorMsg(tracker) {
      return tracker.error ?? 'Unknown Error';
    },
    filterGroup({ group, error }) {
      return group.filter((item) => (error ? !item.isValid : item.isValid));
    },
    getIssueTitles(issue) {
      const titles = new Set();
      for (const tracker of issue.trackers) {
        titles.add(tracker.contentItem.title);
      }
      return titles;
    },
    statusType(type) {
      switch (type) {
        case 'success':
          return 'fas fa-check-circle success';
        case 'warning':
          return 'fas fa-exclamation-triangle warning';
        case 'error':
          return 'fas fa-times-circle error';
        default:
          return 'spinner';
      }
    },
  },
  async mounted() {
    this.showModal();
    this.$root.$on('bv::modal::hide', () => {
      this.$emit('closeModal');
    });
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .modal-title {
    width: 90%;
    .content-item-title {
      display: block;
      width: 100%;
      font-size: 13px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .success {
    color: #2ac873;
  }
  .warning {
    color: #e7b935;
  }
  .error {
    color: #df5555;
  }
}
.thirdPartyTagGroup {
  margin: 20px 0 0 0;
}
.thirdPartyTag {
  display: flex;
  align-items: center;
  margin: 5px 0;
  padding: 5px;
  border: solid 1px #ddd;
  border-radius: 5px;
  .statusIcon {
    font-size: 22px;
  }
  p {
    margin: 1em 0;
  }
}
::v-deep .overflow-wrap, p {
  word-break: break-all;
}
</style>
